import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { adminAPI, apiClient, InvoiceCreateRecoveryStatus, paymentRequestAPI } from '../../services/api';
import { openResolvedExternalUrl } from '../../utils/externalWindow';
import { Invoice } from '../../types/invoice';
import InvoiceDrawer from './InvoiceDrawer';
import InvoiceCreateRecoveryReview from './InvoiceCreateRecoveryReview';

type InvoiceDrawerHostProps = {
  invoice: Partial<Invoice> & { id?: string } | null;
  open: boolean;
  onClose: () => void;
  onInvoiceUpdated?: () => void | Promise<void>;
  isPlatformAdmin?: boolean;
};

function recoveryMessage(value: unknown): string {
  return (typeof value === 'string' ? value : 'Invoice sync requires review.').replace(/https?:\/\/\S+/gi, '[private link]')
    .replace(/\b(?:acct|cus|in|pi|seti|sk|rk)_[A-Za-z0-9_]+\b/g, '[provider reference]');
}

export function needsInvoiceSyncRecovery(invoice: Invoice): boolean {
  const amountDue = Number(invoice.amount_due);
  return invoice.status === 'open' && Number.isFinite(amountDue) && amountDue > 0 &&
    !invoice.grouped_payment_request_is_active &&
    (!invoice.stripe_invoice_id || !invoice.payment_url);
}

export function InvoiceSyncRecovery({ invoice, onRefresh, compact = false }: {
  invoice: Invoice;
  onRefresh: () => Promise<Invoice | undefined>;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const needed = needsInvoiceSyncRecovery(invoice);
  const mutationKey = ['invoice-sync-retry', invoice.id];
  const retrying = React.useSyncExternalStore(
    React.useCallback((notify) => queryClient.getMutationCache().subscribe(notify), [queryClient]),
    () => queryClient.isMutating({ mutationKey }) + queryClient.isMutating({ mutationKey: ['invoice-create-recovery', invoice.id] }),
  ) > 0;
  const syncQuery = useQuery(
    ['invoice-sync-state', invoice.id],
    async () => (await adminAPI.getInvoiceSyncState(invoice.id)).data,
    {
      enabled: needed,
      retry: false,
      refetchInterval: (data) => ['pending', 'processing'].includes(data?.sync_job?.status) ? 3000 : false,
      onSuccess: (data) => {
        if (needed && data?.payment_ready) void onRefresh().catch(() => {});
      },
    },
  );
  const job = syncQuery.data?.sync_job;
  // Missing methods are tolerated by legacy consumers that mock only normal sync.
  const hasRecoveryStatus = typeof adminAPI.getInvoiceCreateRecovery === 'function';
  const recoveryQuery = useQuery(
    ['invoice-create-recovery-status', invoice.id],
    async () => (await adminAPI.getInvoiceCreateRecovery(invoice.id)).data,
    { enabled: needed && Boolean(invoice.id) && hasRecoveryStatus, retry: false },
  );
  const recovery = recoveryQuery.data;
  const auditedRecovery = recovery?.eligible === true && !recoveryQuery.isError;
  const generationBlocked = Boolean(recovery?.generation && recovery.generation.state !== 'checkpointed');
  const benignRecoveryReason = ['disabled', 'not_cached_create_failure'].includes(recovery?.reason || '');
  const recoveryNeedsReview = !auditedRecovery && (generationBlocked || Boolean(recovery?.reason && !benignRecoveryReason && recovery?.generation?.state !== 'checkpointed'));
  const recoveryUnavailable = hasRecoveryStatus && (recoveryQuery.isLoading || recoveryQuery.isError || !recovery);
  const running = ['pending', 'processing'].includes(job?.status);
  const retryMutation = useMutation(async () => {
    let failure: any;
    let refreshed: Invoice | undefined;
    let paymentReady = false;
    try {
      paymentReady = (await adminAPI.retryInvoiceSync(invoice.id)).data?.payment_ready === true;
    } catch (error) {
      failure = error;
    } finally {
      // A failed request may still have persisted local/provider progress.
      const results = await Promise.allSettled([
        onRefresh(), syncQuery.refetch(), hasRecoveryStatus ? recoveryQuery.refetch() : Promise.resolve(),
      ]);
      if (results[0].status === 'fulfilled') refreshed = results[0].value;
    }
    if (failure) {
      toast.error(recoveryMessage(failure?.response?.data?.message || 'Stripe sync retry failed. Check the invoice sync status.'));
    } else if (paymentReady && refreshed?.id === invoice.id && refreshed.status === 'open' && refreshed.stripe_invoice_id && refreshed.payment_url) {
      toast.success('Invoice is ready for payment');
    } else {
      toast.error('Payment readiness is not confirmed. Check the invoice sync status before sending a payment request.');
    }
  }, { mutationKey, retry: false });

  if (!needed) return null;
  return (
    <div role="status" className={compact
      ? 'mt-1 max-w-xs space-y-1 break-words text-xs text-error'
      : 'rounded-md border border-error/40 bg-error/5 px-4 py-3 text-sm text-on-surface'}>
      <p className="font-semibold">Not ready for payment</p>
      {!compact && <p>This invoice is open locally, but its Stripe invoice or payment link is missing.</p>}
      {auditedRecovery && recovery?.reason === 'reconciliation_only' && <p>Review can only reuse a verified existing Stripe invoice. Another create is blocked.</p>}
      {job && <p className="break-all">Stripe sync: {job.status}{job.last_error ? `: ${recoveryMessage(job.last_error)}` : ''}</p>}
      {!auditedRecovery && recovery?.generation?.state === 'checkpointed' && <p>Provider invoice recovered. Retry Stripe sync to finish.</p>}
      {recoveryNeedsReview && <p>Invoice recovery needs review. Refresh status and contact your billing administrator before trying again.</p>}
      {syncQuery.isError && <p>Sync status unavailable. Refresh before retrying.</p>}
      {recoveryQuery.isError && <p>Recovery status unavailable. Refresh before continuing.</p>}
      {auditedRecovery && recovery ? <InvoiceCreateRecoveryReview key={invoice.id} invoice={invoice} status={recovery}
        onRefresh={onRefresh}
        onRefreshStatus={async () => {
          const result = await recoveryQuery.refetch();
          if (result.isError) throw result.error;
          return result.data;
        }}
        onRefreshSync={() => syncQuery.refetch()} /> : !generationBlocked && <button
        type="button"
        aria-label={`${retrying || running ? 'Stripe sync in progress' : 'Retry Stripe sync'} for ${invoice.invoice_number}`}
        className="btn-secondary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
        disabled={retrying || running || syncQuery.isFetching || syncQuery.isError || recoveryUnavailable || recoveryQuery.isFetching}
        onClick={(event) => {
          event.stopPropagation();
          const latest = queryClient.getQueryData<InvoiceCreateRecoveryStatus>(['invoice-create-recovery-status', invoice.id]);
          if (latest?.eligible || (latest?.generation && latest.generation.state !== 'checkpointed')) return;
          if (!queryClient.isMutating({ mutationKey }) && !queryClient.isMutating({ mutationKey: ['invoice-create-recovery', invoice.id] }) &&
              !running && !auditedRecovery && !generationBlocked && !recoveryUnavailable) retryMutation.mutate();
        }}
      >
        <ArrowPathIcon className={`h-4 w-4${retrying || running ? ' animate-spin' : ''}`} />
        {retrying || running ? 'Sync in progress' : 'Retry Stripe sync'}
      </button>}
      {(syncQuery.isError || recoveryQuery.isError) && <button type="button" className="btn-secondary text-xs" onClick={(event) => {
        event.stopPropagation();
        void syncQuery.refetch();
        if (hasRecoveryStatus) void recoveryQuery.refetch();
      }}>Refresh sync status</button>}
    </div>
  );
}

function getPublicUrl(payload: any): string | null {
  return (
    payload?.public_url ||
    payload?.publicUrl ||
    payload?.payment_url ||
    payload?.paymentUrl ||
    null
  );
}

function formatActivityTimestamp(value: string | number | null | undefined) {
  if (!value) return 'N/A';
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function activitySortValue(value: string | number | null | undefined) {
  if (!value) return 0;
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function getStripeObjectUrl(objectId: string | null | undefined, connectedAccountId?: string | null) {
  if (!objectId) return null;
  const accountParam = connectedAccountId ? `?account=${encodeURIComponent(connectedAccountId)}` : '';
  if (objectId.startsWith('in_')) return `https://dashboard.stripe.com/invoices/${objectId}${accountParam}`;
  if (objectId.startsWith('pi_')) return `https://dashboard.stripe.com/payments/${objectId}${accountParam}`;
  if (objectId.startsWith('ch_')) return `https://dashboard.stripe.com/payments/${objectId}${accountParam}`;
  return `https://dashboard.stripe.com/search?query=${encodeURIComponent(objectId)}${accountParam}`;
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

export default function InvoiceDrawerHost({
  invoice,
  open,
  onClose,
  onInvoiceUpdated,
  isPlatformAdmin = false,
}: InvoiceDrawerHostProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const invoiceId = invoice?.id || null;
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);

  const invoiceQuery = useQuery(
    ['invoice-drawer-host-invoice', invoiceId],
    async () => {
      const response = await adminAPI.getInvoice(invoiceId!);
      return (response.data?.invoice || response.data) as Invoice;
    },
    {
      enabled: open && Boolean(invoiceId),
      initialData: invoiceId ? (invoice as Invoice) : undefined,
      staleTime: 30_000,
    },
  );

  const selectedInvoice = invoiceQuery.data as Invoice | undefined;

  const paymentActivityQuery = useQuery(
    ['invoice-drawer-host-payment-activity', invoiceId],
    async () => {
      const response = await adminAPI.getInvoicePaymentActivity(invoiceId!);
      return response.data;
    },
    {
      enabled: open && Boolean(invoiceId),
      retry: false,
    },
  );

  const feeBreakdown = useMemo(() => {
    if (!selectedInvoice || selectedInvoice.status !== 'paid') return null;
    const live = paymentActivityQuery.data?.live_fee_breakdown;
    if (live) {
      return {
        stripeFee: live.stripe_fee,
        platformFee: live.platform_fee,
        totalFees: live.total_fees,
        netAmount: live.net_amount,
        amountPaid: live.amount_paid,
        source: 'live' as const,
      };
    }
    if (
      selectedInvoice.stripe_fee == null &&
      selectedInvoice.platform_fee == null &&
      selectedInvoice.net_amount == null
    ) {
      return null;
    }
    return {
      stripeFee: selectedInvoice.stripe_fee ?? null,
      platformFee: selectedInvoice.platform_fee ?? null,
      totalFees:
        selectedInvoice.stripe_fee != null || selectedInvoice.platform_fee != null
          ? Number(selectedInvoice.stripe_fee || 0) + Number(selectedInvoice.platform_fee || 0)
          : null,
      netAmount: selectedInvoice.net_amount ?? null,
      amountPaid: selectedInvoice.amount_paid || selectedInvoice.total || 0,
      source: 'local' as const,
    };
  }, [paymentActivityQuery.data, selectedInvoice]);

  async function refresh() {
    const [refreshed] = await Promise.all([
      queryClient.fetchQuery(['invoice-drawer-host-invoice', invoiceId], async () => {
        const response = await adminAPI.getInvoice(invoiceId!);
        return (response.data?.invoice || response.data) as Invoice;
      }, { staleTime: 0, retry: false }),
      queryClient.invalidateQueries(['invoice-drawer-host-payment-activity', invoiceId]),
      queryClient.invalidateQueries('admin-invoices'),
      onInvoiceUpdated?.(),
    ]);
    return refreshed;
  }

  async function generatePublicLink(options: { open?: boolean } = {}) {
    if (!invoiceId) return;
    try {
      if (options.open) {
        await openResolvedExternalUrl(async () => {
          const response = await adminAPI.getInvoicePublicLink(invoiceId);
          const url = getPublicUrl(response.data);
          if (!url) throw new Error('No public link was returned for this invoice.');
          await refresh();
          return url;
        });
      } else {
        const response = await adminAPI.getInvoicePublicLink(invoiceId);
        const url = getPublicUrl(response.data);
        if (!url) {
          toast.error('No public link was returned for this invoice.');
          return;
        }
        await refresh();
        await navigator.clipboard.writeText(url);
        toast.success('Invoice link copied');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to generate invoice link');
    }
  }

  async function getGroupedPaymentRequestLink() {
    const requestId = selectedInvoice?.grouped_payment_request_id;
    if (!requestId) {
      throw new Error('No grouped payment request is linked to this invoice.');
    }

    const response = await paymentRequestAPI.generateLink(requestId);
    const url = getPublicUrl(response.data);
    if (!url) {
      throw new Error('No grouped payment link was returned.');
    }
    await refresh();
    return url;
  }

  async function handleAttachmentUpload(file: File) {
    if (!invoiceId) return;
    setUploadingAttachment(true);
    try {
      await adminAPI.uploadInvoiceAttachment(invoiceId, file);
      toast.success('Attachment uploaded');
      await refresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleAttachmentDownload(attachmentId: string, originalName: string) {
    if (!invoiceId) return;
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to download attachment');
    }
  }

  async function handleAttachmentDelete(attachmentId: string) {
    if (!invoiceId) return;
    try {
      await adminAPI.deleteInvoiceAttachment(invoiceId, attachmentId);
      toast.success('Attachment deleted');
      await refresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete attachment');
    }
  }

  if (!open || !selectedInvoice) return null;

  const groupedPaymentLockActive = Boolean(selectedInvoice.grouped_payment_request_is_active);
  const groupedPaymentRequestId = selectedInvoice.grouped_payment_request_id;

  const actions = [
    groupedPaymentLockActive && groupedPaymentRequestId
      ? {
          label: 'Copy grouped link',
          onClick: async () => {
            try {
              const url = await getGroupedPaymentRequestLink();
              await navigator.clipboard.writeText(url);
              toast.success('Grouped payment link copied');
            } catch (error: any) {
              toast.error(error?.response?.data?.message || error?.message || 'Failed to copy grouped payment link');
            }
          },
        }
      : null,
    groupedPaymentLockActive && groupedPaymentRequestId
      ? {
          label: 'Open grouped request',
          onClick: async () => {
            try {
              const url = await getGroupedPaymentRequestLink();
              window.open(url, '_blank', 'noopener,noreferrer');
            } catch (error: any) {
              toast.error(error?.response?.data?.message || error?.message || 'Failed to open grouped payment request');
            }
          },
        }
      : null,
    !groupedPaymentLockActive && selectedInvoice.status === 'draft'
      ? {
          label: 'Finalize',
          onClick: async () => {
            try {
              await adminAPI.finalizeInvoice(selectedInvoice.id);
              toast.success('Invoice finalized. Check payment readiness before sending.');
            } catch (error: any) {
              toast.error(error?.response?.data?.message || 'Failed to finalize invoice');
            } finally {
              await refresh().catch(() => toast.error('Failed to refresh invoice. Reload to check its saved status.'));
            }
          },
        }
      : null,
    !groupedPaymentLockActive
      ? {
          label: 'Copy public link',
          onClick: () => generatePublicLink(),
        }
      : null,
    !groupedPaymentLockActive
      ? {
          label: 'Open public view',
          onClick: () => {
            const url = selectedInvoice.payment_url;
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else {
              void generatePublicLink({ open: true });
            }
          },
        }
      : null,
    !groupedPaymentLockActive && selectedInvoice.status === 'open' && !needsInvoiceSyncRecovery(selectedInvoice)
      ? {
          label: 'Send',
          onClick: async () => {
            await adminAPI.sendInvoice(selectedInvoice.id);
            toast.success('Invoice sent');
            await refresh();
          },
        }
      : null,
    !groupedPaymentLockActive && selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'void'
      ? {
          label: 'Void',
          dangerous: true,
          onClick: async () => {
            await adminAPI.voidInvoice(selectedInvoice.id);
            toast.success('Invoice voided');
            await refresh();
          },
        }
      : null,
  ].filter(Boolean) as Array<{ label: string; dangerous?: boolean; onClick: () => void | Promise<void> }>;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleAttachmentUpload(file);
        }}
      />
      <InvoiceDrawer
        open={open}
        selectedInvoice={selectedInvoice}
        syncWarning={<InvoiceSyncRecovery invoice={selectedInvoice} onRefresh={refresh} />}
        paymentActivityLoading={paymentActivityQuery.isLoading}
        paymentActivity={paymentActivityQuery.data || null}
        feeBreakdown={feeBreakdown}
        formatActivityTimestamp={formatActivityTimestamp}
        activitySortValue={activitySortValue}
        getStripeObjectUrl={getStripeObjectUrl}
        onClose={onClose}
        onAction={() => {}}
        actions={actions}
        onUploadFileClick={() => fileInputRef.current?.click()}
        uploadingAttachment={uploadingAttachment}
        onAttachmentDownload={handleAttachmentDownload}
        onAttachmentDelete={handleAttachmentDelete}
        formatFileSize={formatFileSize}
        isPlatformAdmin={isPlatformAdmin}
        onSaveEdit={async (payload) => {
          await apiClient.put(`/invoices/admin/${selectedInvoice.id}`, payload);
          toast.success('Invoice updated');
          await refresh();
        }}
        onMarkPaidSubmit={async (payload) => {
          setMarkPaidSubmitting(true);
          try {
            await adminAPI.markInvoicePaid(selectedInvoice.id, payload);
            toast.success('Invoice marked paid');
            await refresh();
          } finally {
            setMarkPaidSubmitting(false);
          }
        }}
        markPaidSubmitting={markPaidSubmitting}
      />
    </>
  );
}
